import API from "../global/api";
import { listPaging } from "../global/Utils";

/**
 * Stock reporting.
 *
 * Both endpoints read the ledger rather than an item column, so the report and
 * the history behind it can never disagree.
 */

const errorMessage = (error, fallback) =>
  error?.response?.data?.message || error?.message || fallback;

// Stock report: item, current quantity, low/out state.
export const getStockReportRequest = () => ({ type: "STOCK_REPORT_REQUEST" });
export const getStockReportSuccess = (data) => ({
  type: "STOCK_REPORT_SUCCESS",
  payload: data,
});
export const getStockReportError = (msg) => ({
  type: "STOCK_REPORT_ERROR",
  payload: msg,
});
export const getStockReport = (page, limit, searchTerm, state) => {
  return (dispatch) => {
    dispatch(getStockReportRequest());
    const paging = listPaging(page, limit);
    API.get(
      `/stock/report?page=${paging.page}&limit=${paging.limit}&search=${searchTerm || ""}${state ? `&state=${state}` : ""}`,
    )
      .then((res) => {
        dispatch(getStockReportSuccess(res.data));
      })
      .catch((error) => {
        dispatch(
          getStockReportError(errorMessage(error, "Failed to load stock")),
        );
      });
  };
};

// The ledger rows behind one item's quantity, newest first. An optional
// { startDate, endDate } window narrows it, and the response carries the
// opening and closing balances for that window alongside the rows.
export const getStockMovementsRequest = () => ({
  type: "STOCK_MOVEMENTS_REQUEST",
});
export const getStockMovementsSuccess = (data) => ({
  type: "STOCK_MOVEMENTS_SUCCESS",
  payload: data,
});
export const getStockMovementsError = (msg) => ({
  type: "STOCK_MOVEMENTS_ERROR",
  payload: msg,
});
export const getStockMovements = (itemId, page, limit, range = {}) => {
  return (dispatch) => {
    dispatch(getStockMovementsRequest());
    const paging = listPaging(page, limit);
    // Either end of the range is optional: an open start reads from the very
    // first movement, an open end reads up to today.
    const window = `${range.startDate ? `&startDate=${range.startDate}` : ""}${
      range.endDate ? `&endDate=${range.endDate}` : ""
    }`;
    API.get(
      `/stock/movements/${itemId}?page=${paging.page}&limit=${paging.limit}${window}`,
    )
      .then((res) => {
        dispatch(getStockMovementsSuccess(res.data));
      })
      .catch((error) => {
        dispatch(
          getStockMovementsError(
            errorMessage(error, "Failed to load stock history"),
          ),
        );
      });
  };
};

export const clearStockMovements = () => ({ type: "STOCK_MOVEMENTS_CLEAR" });

// Printing the ledger.
export const exportStockLedgerRequest = () => ({
  type: "EXPORT_STOCK_LEDGER_REQUEST",
});
export const exportStockLedgerDone = () => ({
  type: "EXPORT_STOCK_LEDGER_DONE",
});

/**
 * Builds the PDF or the Excel of one product's ledger.
 *
 * Re-reads the window instead of printing the rows already in the store: the
 * screen holds one page of them, and a ledger that silently stops partway is
 * worse than no ledger at all. Page 0 with the full count also means the server
 * computes a running balance for every row, which it only does for a first page.
 */
export const exportStockLedger = (
  itemId,
  range,
  type,
  details,
  handleVisible,
  handleDangerVisible,
) => {
  return (dispatch) => {
    dispatch(exportStockLedgerRequest());
    const window = `${range?.startDate ? `&startDate=${range.startDate}` : ""}${
      range?.endDate ? `&endDate=${range.endDate}` : ""
    }`;
    API.get(`/stock/movements/${itemId}?page=0&limit=100000${window}`)
      .then(async (res) => {
        const movements = res.data?.data || [];
        if (movements.length === 0) {
          dispatch(exportStockLedgerDone());
          handleDangerVisible("No Data Found");
          return;
        }
        // The balances come from the same response as the rows, so the printed
        // document can never pair one window's rows with another's totals.
        const meta = {
          ...details,
          openingBalance: res.data?.openingBalance ?? 0,
          closingBalance: res.data?.closingBalance ?? 0,
        };
        // Loaded on demand - neither jsPDF nor ExcelJS is small, and most
        // visits to the ledger never print it.
        const printers = await import(
          "../Screens/Stock/ledgerPrintFunctions"
        );
        if (type === "PDF") {
          await printers.generateStockLedgerPdf(movements, meta);
          handleVisible("PDF Created & Ready to Download Successfully");
        } else {
          await printers.generateStockLedgerExcel(movements, meta);
          handleVisible("Downloaded Successfully");
        }
        dispatch(exportStockLedgerDone());
      })
      .catch((error) => {
        dispatch(exportStockLedgerDone());
        handleDangerVisible(errorMessage(error, "Failed to print the ledger"));
      });
  };
};

// A manual correction. Written to the ledger like everything else, so the count
// still reconciles to the history that produced it.
export const adjustStockRequest = () => ({ type: "ADJUST_STOCK_REQUEST" });
export const adjustStockSuccess = (data) => ({
  type: "ADJUST_STOCK_SUCCESS",
  payload: data,
});
export const adjustStockError = (msg) => ({
  type: "ADJUST_STOCK_ERROR",
  payload: msg,
});
export const adjustStock = (
  data,
  handleVisible,
  handleDangerVisible,
  onSuccess,
) => {
  return (dispatch) => {
    dispatch(adjustStockRequest());
    API.post(`/stock/adjust`, data)
      .then((res) => {
        dispatch(adjustStockSuccess(res.data));
        handleVisible(res.data.message);
        if (onSuccess) onSuccess(res.data);
      })
      .catch((error) => {
        const message = errorMessage(error, "Failed to adjust stock");
        dispatch(adjustStockError(message));
        handleDangerVisible(message);
      });
  };
};
